import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  DialogRef,
  DialogService,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import {
  ContentRecord,
  Hunt,
  HuntPackageRecord,
  HuntPackagesAdded,
  HuntTemplate,
} from '@cyborg-monorepo/hunter/common';
import Mark from 'mark.js';
import { map } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { AuthService } from '../../../../auth/auth.service';
import { FeatureFlagService } from '../../../../helpers/feature-flag.service';
import {
  gatherTags,
  isHuntPackageHuntCompatible,
} from '../../../../helpers/utils';
import { SearchStateService } from '../../../research/services/search-state.service';
import { CalendlyService } from '../../services/calendly.service';
import { FavoriteService } from '../../services/favorite.service';
import { CyborgContentComponent } from '../cyborg-content/cyborg-content.component';
import {
  HuntPackAddedToHuntComponent,
  HuntPackAddedToTemplateComponent,
} from '../cyborg-snackbar/cyborg-snackbar';
import { AddToQuickHuntDialogComponent } from '../dialogs/add-to-quick-hunt-dialog/add-to-quick-hunt-dialog.component';
import { AddToTemplateDialogComponent } from '../dialogs/add-to-template-dialog/add-to-template-dialog.component';
import { HuntModuleDialogComponent } from '../dialogs/hunt-module-dialog/hunt-module-dialog.component';
import { NewHuntDialogComponent } from '../dialogs/new-hunt-dialog/new-hunt-dialog.component';
import { NewTemplateDialogComponent } from '../dialogs/new-template-dialog/new-template-dialog.component';
import { FacetButtonItem } from '../facet-buttons/facet-buttons.component';
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service';

@Component({ standalone: false, 
  selector: 'hunt-package',
  templateUrl: './hunt-package.component.html',
  styleUrls: ['./hunt-package.component.scss'],
})
export class HuntPackageComponent implements OnInit, AfterViewInit {
  @ViewChild(CyborgContentComponent)
  public cyborgContent: CyborgContentComponent;
  public locked$: Observable<boolean>;
  @Input() public data: HuntPackageRecord;
  public contents: Array<ContentRecord>;
  public favorited: boolean;
  public tags: FacetButtonItem[];
  public huntCompatible: boolean = false;
  public huntModuleAccess: boolean = false;

  public options = [
    {
      actionName: 'Existing Hunt Template',
      click: (): void => {
        this.addToExistingTemplate();
      },
    },
    {
      actionName: 'Existing Hunt',
      click: (): void => {
        this.addToExistingQuickHunt();
      },
    },
    {
      actionName: 'New Hunt Template',
      click: (): void => {
        this.addToNewTemplate();
      },
    },
    {
      actionName: 'New Hunt',
      click: (): void => {
        this.addToNewQuickHunt();
      },
    },
  ];

  constructor(
    private myElement: ElementRef,
    private searchStateService: SearchStateService,
    private authService: AuthService,
    private favoriteService: FavoriteService,
    private dialogService: DialogService,
    public router: Router,
    private featureFlagService: FeatureFlagService,
    private snackBarService: MatSnackBar,
    public calendlyService: CalendlyService,
    public trackingService: TrackingService,
  ) {}

  public ngOnInit(): void {
    this.locked$ = this.authService.user$.pipe(
      map((profile) => {
        const inTrial = profile.company.status === 'trial';
        return !(!inTrial || this.data.community);
      }),
    );

    this.isFavorited();
    this.isHuntCompatible();
    this.huntModuleAccess = this.featureFlagService.huntModuleAccess();
    this.tags = gatherTags(this.data);
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.textHightlighting();
    }, 0);
  }

  private isFavorited(): void {
    this.favoriteService
      .checkThreatProfileFavorite(this.data.UUID)
      .subscribe((favorite: boolean) => {
        this.favorited = favorite;
      });
  }

  private isHuntCompatible(): void {
    this.huntCompatible = isHuntPackageHuntCompatible(this.data);
  }

  public openHuntDialog(): void {
    this.trackingService.trackEvent(
      TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_OPENED,
    );
    const dialogRef: DialogRef = this.dialogService.open(
      HuntModuleDialogComponent,
      {
        panelClass: 'full-panel',
      },
    );

    dialogRef.afterClosed().subscribe((talkToCyborg: boolean) => {
      if (talkToCyborg) {
        this.talkToCyborg();
      } else {
        this.trackingService.trackEvent(
          TrackingEventEnum.COMMUNITY_HUNT_MODULE_DIALOG_ABANDONED,
        );
      }
    });
  }

  public talkToCyborg(): void {
    this.calendlyService.talkToCyborg();
  }

  public favorite(): void {
    this.favoriteService.favoriteHuntPackage(this.data.UUID).subscribe(() => {
      this.favorited = true;
      this.favoriteService.setUpdated(true);
    });
  }

  public unfavorite(): void {
    this.favoriteService.unfavoriteHuntPackage(this.data.UUID).subscribe(() => {
      this.favorited = false;
      this.favoriteService.setUpdated(true);
    });
  }

  public onOpen(target: HTMLElement): void {
    const huntPackage =
      target.parentElement.parentElement.parentElement.parentElement
        .parentElement;
    huntPackage.classList.toggle('is-opened');
  }

  public addToExistingQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToQuickHuntDialogComponent,
      { data: { huntPackages: [this.data] } },
    );

    dialogRef.afterClosed().subscribe((huntMeta: HuntPackagesAdded) => {
      if (huntMeta)
        this.snackBarService.openFromComponent(HuntPackAddedToHuntComponent, {
          duration: 5 * 1000,
          data: {
            id: huntMeta.id,
            name: huntMeta.name,
            huntPackagesAdded: huntMeta.huntPackagesAdded,
          },
        });
    });
  }

  public addToNewQuickHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewHuntDialogComponent,
      { data: { huntPackages: [this.data] } },
    );
    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        if(this.featureFlagService.huntRedesignAccess()) {
          this.router.navigate(['hunt-redesign', hunt.id, 'workbench']);
        } else {
          if (hunt.huntPackages.length >= 1)
            this.router.navigate([
              'hunt',
              hunt.id,
              'details',
              hunt.huntPackages[0].UUID,
            ]);
          else this.router.navigate(['hunt', hunt.id]);
        }
      }
    });
  }

  public addToExistingTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      AddToTemplateDialogComponent,
      { data: { huntPackages: [this.data] } },
    );

    dialogRef.afterClosed().subscribe((templateMeta: HuntPackagesAdded) => {
      if (templateMeta)
        this.snackBarService.openFromComponent(
          HuntPackAddedToTemplateComponent,
          {
            duration: 5 * 1000,
            data: {
              id: templateMeta.id,
              name: templateMeta.name,
              huntPackagesAdded: templateMeta.huntPackagesAdded,
            },
          },
        );
    });
  }

  public addToNewTemplate(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      NewTemplateDialogComponent,
      { data: { huntPackages: [this.data] } },
    );
    dialogRef.afterClosed().subscribe((result: HuntTemplate) => {
      if (result) this.router.navigate(['hunt', 'hunt-template', result.id]);
    });
  }

  private textHightlighting(): void {
    const context = this.myElement.nativeElement.querySelectorAll(
      'package-description, header-title',
    );
    const instance = new Mark(context);
    const term = this.searchStateService.state.value.term;
    if (term) {
      instance.mark(term, {
        element: 'em',
        className: 'highlight',
      });
    }
  }
}
